import { useEffect, useState } from 'react';
import { getPaperPredador, resetPaperPredador, savePaperPredador } from '../../services/PaperPredadorService';

function money(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '—';
    return number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function signed(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '—';
    const prefix = number > 0 ? '+' : '';
    return `${prefix}${money(number)}`;
}

function when(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function apiError(err) {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data.message === 'string') return data.message;
    return err?.message || 'Não foi possível salvar a simulação.';
}

function PaperSim() {
    const [book, setBook] = useState(null);
    const [enabled, setEnabled] = useState(false);
    const [cash, setCash] = useState('');
    const [dailyStop, setDailyStop] = useState(false);
    const [dailyStopUsdc, setDailyStopUsdc] = useState('');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState('');

    function applyForm(next) {
        setEnabled(Boolean(next?.enabled));
        setDailyStop(Boolean(next?.dailyStop));
        setDailyStopUsdc(next?.dailyStopUsdc ? String(Number(next.dailyStopUsdc)) : '');
        setCash(current => {
            if (current !== '') return current;
            const seed = next?.startedAt ? (next?.equity || next?.cash) : next?.suggestedCash;
            const number = Number(seed);
            return Number.isFinite(number) && number > 0 ? String(Number(number.toFixed(2))) : '';
        });
    }

    useEffect(() => {
        let alive = true;
        let primed = false;
        function load() {
            getPaperPredador()
                .then(result => {
                    if (!alive) return;
                    setBook(result);
                    if (!primed) {
                        primed = true;
                        applyForm(result);
                        setLoading(false);
                    }
                })
                .catch(err => {
                    if (!alive) return;
                    setError(apiError(err));
                    if (!primed) setLoading(false);
                });
        }
        load();
        const timer = setInterval(load, 20000);
        return () => {
            alive = false;
            clearInterval(timer);
        };
    }, []);

    async function onSave() {
        if (busy) return;
        setBusy(true);
        setError('');
        setSaved('');
        try {
            const next = await savePaperPredador({
                enabled,
                cash: cash === '' ? null : Number(String(cash).replace(',', '.')),
                dailyStop,
                dailyStopUsdc: dailyStop ? Number(String(dailyStopUsdc).replace(',', '.')) : null
            });
            setBook(next);
            applyForm(next);
            setSaved(enabled ? 'Simulação ligada.' : 'Simulação desligada. O livro ficou guardado.');
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    }

    async function onReset() {
        if (busy) return;
        const amount = Number(String(cash).replace(',', '.'));
        if (!(amount >= 5)) {
            setError('Informe pelo menos 5 USDC para recomeçar.');
            return;
        }
        if (!window.confirm(`Zerar a simulação com ${money(amount)} USDC? O histórico de papel some.`))
            return;
        setBusy(true);
        setError('');
        setSaved('');
        try {
            const next = await resetPaperPredador(amount);
            setBook(next);
            applyForm(next);
            setEnabled(true);
            setSaved('Simulação recomeçada.');
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    }

    const started = Boolean(book?.startedAt);

    return (
        <section className="paper-sim">
            <div className="paper-sim-head">
                <div>
                    <h2>Simulação</h2>
                    <p>
                        O mesmo Predador, com USDC de papel. A cada 90 segundos ele lê o radar, compra e vende
                        com as regras da opção ligada. Nenhuma ordem vai para a Binance. Cada ponta desconta 0,1% de taxa.
                    </p>
                </div>
            </div>

            {loading ? <p className="paper-muted">Lendo o livro...</p> : null}
            {error ? <p className="predators-error">{error}</p> : null}
            {saved ? <p className="paper-saved">{saved}</p> : null}

            <label className="paper-check">
                <input
                    type="checkbox"
                    checked={enabled}
                    onChange={event => setEnabled(event.target.checked)}
                />
                Ligar simulação
            </label>

            <div className="paper-fields">
                <label>
                    {started ? 'USDC para zerar' : 'USDC da simulação'}
                    <input
                        type="number"
                        min="5"
                        step="0.01"
                        value={cash}
                        onChange={event => setCash(event.target.value)}
                    />
                </label>
                <span className="paper-muted">
                    {started
                        ? 'O saldo livre está no quadro abaixo. Este número só entra quando você zera o livro.'
                        : 'Mínimo de 5 USDC. O saldo entra na primeira vez que você liga.'}
                </span>
            </div>

            <label className="paper-check">
                <input
                    type="checkbox"
                    checked={dailyStop}
                    onChange={event => setDailyStop(event.target.checked)}
                />
                Stop diário
            </label>
            {dailyStop ? (
                <div className="paper-fields">
                    <label>
                        Perda máxima do dia (USDC)
                        <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={dailyStopUsdc}
                            onChange={event => setDailyStopUsdc(event.target.value)}
                        />
                    </label>
                    <span className="paper-muted">
                        Com a marca, compra nova para quando o prejuízo realizado de hoje chega nesse valor.
                        O trade que já está aberto segue até a reversão ou o stop dele. Amanhã a caça volta.
                        Vale para o livro de papel e para o trade ao vivo, cada um com o próprio prejuízo.
                    </span>
                </div>
            ) : (
                <p className="paper-muted">Sem a marca, o dia segue mesmo depois de um prejuízo.</p>
            )}

            {started ? (
                <div className="paper-stats">
                    <div><span>Livre</span><strong>{money(book?.cash)}</strong></div>
                    <div><span>Em posição</span><strong>{money(book?.deployed)}</strong></div>
                    <div><span>Resultado aberto</span><strong>{signed(book?.openPnl)}</strong></div>
                    <div><span>Realizado hoje</span><strong>{signed(book?.dayPnl)}</strong></div>
                    <div><span>Realizado no livro</span><strong>{signed(book?.realized)}</strong></div>
                </div>
            ) : null}

            {book?.dayStopHit ? (
                <p className="paper-warn">A simulação parou compra nova até amanhã. O prejuízo realizado de hoje chegou no stop diário.</p>
            ) : null}
            {book?.liveStopHit ? (
                <p className="paper-warn">O trade ao vivo parou compra nova até amanhã. O cofre realizou {signed(book?.liveDayPnl)} USDC hoje.</p>
            ) : null}

            {book?.open?.length ? (
                <ul className="paper-list">
                    {book.open.map(row => (
                        <li key={row.id}>
                            <strong>{row.pair}</strong>
                            <span>{row.status === 'IN_POSITION' ? 'Em posição' : 'Esperando sinal'}</span>
                            <span>{row.status === 'IN_POSITION' ? signed(row.pnl) : (row.chance != null ? `${row.chance}%` : '')}</span>
                        </li>
                    ))}
                </ul>
            ) : null}

            {book?.closed?.length ? (
                <ul className="paper-list closed">
                    {book.closed.map(row => (
                        <li key={row.id}>
                            <strong>{row.pair}</strong>
                            <span>{row.exitLabel}</span>
                            <span>{signed(row.pnl)} · {when(row.closedAt)}</span>
                        </li>
                    ))}
                </ul>
            ) : null}

            <div className="paper-actions">
                <button type="button" className="predators-start" onClick={onSave} disabled={busy || loading}>
                    {busy ? 'Salvando...' : 'Salvar simulação'}
                </button>
                {started ? (
                    <button type="button" className="predators-secondary" onClick={onReset} disabled={busy}>
                        Zerar livro
                    </button>
                ) : null}
            </div>
        </section>
    );
}

export default PaperSim;
